export type AuthOption = 'email_password' | 'google' | 'apple';

const AUTH_OPTION_SELECTORS: Array<[AuthOption, string]> = [
  ['email_password', 'input[type="password"]'],
  ['google', '[data-auth-option="google"]'],
  ['apple', '[data-auth-option="apple"]'],
];

const isVisible = (element: Element) => {
  let current: Element | null = element;

  while (current) {
    if (
      current instanceof HTMLElement &&
      (current.hidden || current.getAttribute('aria-hidden') === 'true')
    ) {
      return false;
    }

    const style = window.getComputedStyle(current);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    current = current.parentElement;
  }

  return true;
};

export const getAuthOptionsFromDom = (
  root: ParentNode = document,
): AuthOption[] =>
  AUTH_OPTION_SELECTORS.flatMap(([option, selector]) => {
    const available = Array.from(root.querySelectorAll(selector)).some(
      isVisible,
    );
    return available ? [option] : [];
  });

const waitForNextPaint = () =>
  new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

export async function waitForAuthOptionsFromDom(): Promise<AuthOption[]> {
  if (typeof document === 'undefined') return [];

  await waitForNextPaint();

  const hasPendingOptions = () =>
    document.querySelector('[data-auth-options-status="loading"]') !== null;

  if (!hasPendingOptions() || typeof MutationObserver === 'undefined') {
    return getAuthOptionsFromDom();
  }

  return new Promise((resolve) => {
    const finish = () => {
      observer.disconnect();
      clearTimeout(timeout);
      resolve(getAuthOptionsFromDom());
    };
    const observer = new MutationObserver(() => {
      if (!hasPendingOptions()) finish();
    });
    const timeout = setTimeout(finish, 10_000);

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['data-auth-options-status'],
    });
  });
}
